import {
  appendBookingTrackingEvent,
  listPendingBookingTrackingKeys,
  readBookingTrackingBuffer,
  tagUntaggedBookingTrackingEvents,
} from '@/components/consultation/bookingTracking/bookingTrackingStorage'
import { flushBookingTrackingToFirestore } from '@/components/consultation/bookingTracking/bookingTrackingFlush'
import {
  BOOKING_TRACKING_FLUSH_THROTTLE_MS,
  type BookingTrackingEvent,
  type BookingTrackingNavigationType,
} from '@/components/consultation/bookingTracking/bookingTrackingTypes'
import createLogger from '@/utils/logger'

const logger = createLogger('components/consultation/bookingTracking/bookingTrackingService')

function describeFlushError(error: unknown): { code?: string; message: string } {
  if (error instanceof Error) {
    const code =
      typeof error === 'object' && error != null && 'code' in error
        ? String((error as { code: unknown }).code)
        : undefined
    return { code, message: error.message }
  }
  if (typeof error === 'object' && error != null && 'code' in error) {
    const record = error as { code?: unknown; message?: unknown }
    return {
      code: record.code != null ? String(record.code) : undefined,
      message: typeof record.message === 'string' ? record.message : String(error),
    }
  }
  return { message: String(error) }
}

let activeConsultationId: string | null = null
let throttleTimer: ReturnType<typeof setTimeout> | null = null
let flushInFlight: Promise<void> | null = null
let lastFlushAttemptAt = 0

function clearThrottleTimer(): void {
  if (throttleTimer != null) {
    clearTimeout(throttleTimer)
    throttleTimer = null
  }
}

function logBookingTrackingFlushFailure(
  context: string,
  consultationId: string,
  pendingKeyCount: number,
  error: unknown,
): void {
  const { code, message } = describeFlushError(error)
  logger.warn(context, {
    consultationId,
    pendingKeyCount,
    code,
    message,
  })
  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn(`[booking-tracking] ${context}`, {
      consultationId,
      pendingKeyCount,
      code,
      message,
    })
  }
}

let groupFlushPromise: Promise<void> | null = null

async function flushPendingEventsGroupedByBufferedConsultationId(): Promise<void> {
  if (groupFlushPromise) {
    await groupFlushPromise
    return
  }

  const run = async (): Promise<void> => {
    const buffer = readBookingTrackingBuffer()
    const keysByConsultationId = new Map<string, string[]>()
    for (const [key, event] of Object.entries(buffer.events)) {
      if (!event?.consultationId) {
        continue
      }
      const list = keysByConsultationId.get(event.consultationId) ?? []
      list.push(key)
      keysByConsultationId.set(event.consultationId, list)
    }
    for (const [consultationId, keys] of keysByConsultationId) {
      if (keys.length === 0) {
        continue
      }
      try {
        await flushBookingTrackingToFirestore(consultationId, keys)
      } catch (error) {
        logBookingTrackingFlushFailure(
          'Booking tracking flush failed for buffered consultation id',
          consultationId,
          keys.length,
          error,
        )
      }
    }
  }

  groupFlushPromise = run()
  try {
    await groupFlushPromise
  } finally {
    groupFlushPromise = null
  }
}

/** Flush any sessionStorage buffer entries that already have a consultation id (e.g. after reload). */
export function bootstrapBufferedBookingTrackingFlush(): void {
  void flushPendingEventsGroupedByBufferedConsultationId()
}

export function setBookingTrackingConsultationId(consultationId: string | null): void {
  if (!consultationId) {
    if (activeConsultationId == null) {
      void flushPendingEventsGroupedByBufferedConsultationId()
      return
    }
    const originalId = activeConsultationId
    void flushBookingTrackingNow().finally(() => {
      if (activeConsultationId === originalId) {
        activeConsultationId = null
      }
    })
    return
  }

  const hadNoId = activeConsultationId == null
  activeConsultationId = consultationId
  if (hadNoId) {
    tagUntaggedBookingTrackingEvents(consultationId)
  }
  scheduleThrottledBookingTrackingFlush()
  if (hadNoId && listPendingBookingTrackingKeys(consultationId).length > 0) {
    void runBookingTrackingFlush()
  }
}

export function getBookingTrackingConsultationId(): string | null {
  return activeConsultationId
}

export function recordBookingTrackingEvent(event: BookingTrackingEvent): void {
  appendBookingTrackingEvent({
    ...event,
    consultationId: event.consultationId ?? activeConsultationId ?? undefined,
  })
  scheduleThrottledBookingTrackingFlush()
}

function resolveConsultationsHubTrackingPath(): string {
  if (typeof window !== 'undefined' && window.location.pathname.includes('/consultations')) {
    return window.location.pathname
  }
  return '/consultations'
}

/** Record UI events for a consultation outside the clinical booking signup shell (e.g. hub cancel). */
export function recordConsultationBookingTracking(
  consultationId: string,
  event: BookingTrackingEvent,
): void {
  setBookingTrackingConsultationId(consultationId)
  recordBookingTrackingEvent({
    ...event,
    consultationId,
    path: event.path ?? resolveConsultationsHubTrackingPath(),
  })
}

export async function flushConsultationBookingTracking(consultationId: string): Promise<void> {
  setBookingTrackingConsultationId(consultationId)
  await flushBookingTrackingNow()
}

export function resolveBookingTrackingNavigationType(): BookingTrackingNavigationType {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
    return 'navigate'
  }
  const nav = performance.getEntriesByType('navigation')[0] as
    PerformanceNavigationTiming | undefined
  const type = nav?.type
  if (type === 'reload' || type === 'back_forward' || type === 'navigate') {
    return type
  }
  if (type === 'prerender') {
    return 'prerender'
  }
  return 'navigate'
}

function scheduleThrottledBookingTrackingFlush(): void {
  if (!activeConsultationId) {
    return
  }
  if (listPendingBookingTrackingKeys(activeConsultationId).length === 0) {
    return
  }

  const now = Date.now()
  const elapsed = now - lastFlushAttemptAt

  if (flushInFlight == null && elapsed >= BOOKING_TRACKING_FLUSH_THROTTLE_MS) {
    void runBookingTrackingFlush()
    return
  }

  if (throttleTimer != null) {
    return
  }

  const wait = Math.max(0, BOOKING_TRACKING_FLUSH_THROTTLE_MS - elapsed)
  throttleTimer = setTimeout(() => {
    throttleTimer = null
    void runBookingTrackingFlush()
  }, wait)
}

/** Persist buffered events now (e.g. when leaving the booking flow). */
export async function flushBookingTrackingNow(): Promise<void> {
  clearThrottleTimer()
  await runBookingTrackingFlush()
}

/** @internal test hook */
export function resetBookingTrackingServiceForTests(): void {
  clearThrottleTimer()
  activeConsultationId = null
  flushInFlight = null
  groupFlushPromise = null
  lastFlushAttemptAt = 0
}

async function runBookingTrackingFlush(): Promise<void> {
  while (flushInFlight) {
    await flushInFlight
  }

  const consultationId = activeConsultationId
  if (!consultationId) {
    return
  }

  const keys = listPendingBookingTrackingKeys(consultationId)
  if (keys.length === 0) {
    return
  }

  lastFlushAttemptAt = Date.now()

  flushInFlight = flushBookingTrackingToFirestore(consultationId, keys)
    .then(() => undefined)
    .catch((error) => {
      logBookingTrackingFlushFailure(
        'Booking tracking flush failed',
        consultationId,
        keys.length,
        error,
      )
    })
    .finally(() => {
      flushInFlight = null
      if (activeConsultationId && listPendingBookingTrackingKeys(activeConsultationId).length > 0) {
        scheduleThrottledBookingTrackingFlush()
      }
    })

  await flushInFlight
}

async function onBookingTrackingPageHide(): Promise<void> {
  // Sequential: both paths read the same buffer; parallel would double-write.
  await flushPendingEventsGroupedByBufferedConsultationId()
  await flushBookingTrackingNow()
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    void onBookingTrackingPageHide()
  })
}
